import { useState } from "react";
import { useReadyForGrowingQueue } from "../hooks/useSowingInstructions";
import type { SowingInstructionDto } from "../services/sowingInstructionsApi";
import { AcknowledgeReceiptDialog } from "../components/sowingInstructions/PhaseWorkflowDialogs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";

const formatQuantity = (value: number) => {
  if (Number.isInteger(value)) {
    return value.toString();
  }
  return value.toFixed(2).replace(/\.00$/, "").replace(/(\.[1-9]*)0+$/, "$1");
};

const PRICING_MODE_PER_SEEDLING = 2;
const UNIT_GRAMS = 2;

const getDisplayUnitName = (
  instruction: SowingInstructionDto,
  unitNameFromMetadata?: string
) => {
  // Legacy fallback: older per-seedling records may carry unit=2 due prior frontend mismatch.
  if (
    instruction.pricingMode === PRICING_MODE_PER_SEEDLING &&
    instruction.unit === UNIT_GRAMS
  ) {
    return "pieces";
  }

  return unitNameFromMetadata?.toLowerCase() || "units";
};

export const ReadyForGrowingPage = () => {
  const queueQuery = useReadyForGrowingQueue();
  const unitsQuery = useUnitsOfMeasure();
  const { hasPermission } = useAuthStore();
  const canAcknowledge = hasPermission("sowing_instructions.acknowledge");
  const [selected, setSelected] = useState<SowingInstructionDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAcknowledge = (instruction: SowingInstructionDto) => {
    setSelected(instruction);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setSelected(null);
    queueQuery.refetch();
  };

  const rows = queueQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Ready for Growing</h1>
        <p className="text-muted-foreground mt-1">
          Germinated stock waiting for Growing Supervisor acknowledgement and greenhouse assignment.
        </p>
      </div>

      {queueQuery.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading queue...</p>
        </div>
      )}

      {queueQuery.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load queue</p>
        </div>
      )}

      {!queueQuery.isLoading && rows.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">No items currently ready for growing</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instruction #</TableHead>
                <TableHead>Crop Type</TableHead>
                <TableHead>Variety</TableHead>
                <TableHead>Tray Type</TableHead>
                <TableHead>Sowing Date</TableHead>
                <TableHead>Germinated Qty</TableHead>
                <TableHead>To Stock</TableHead>
                <TableHead>Filling Reserve</TableHead>
                <TableHead>Ready Since</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inst) => {
                const quantityGerminated =
                  typeof inst.quantityGerminated === "number"
                    ? inst.quantityGerminated
                    : typeof inst.quantitySown === "number"
                      ? inst.quantitySown
                      : inst.quantityToSow;

                const stockQty =
                  typeof inst.stockQuantity === "number"
                    ? inst.stockQuantity
                    : Math.floor(quantityGerminated * 0.7);

                const reserveQty =
                  typeof inst.fillingReserve === "number"
                    ? inst.fillingReserve
                    : quantityGerminated - stockQty;

                const unitNameFromMetadata = unitsQuery.data?.find((u) => u.value === inst.unit)?.name;
                const unitName = getDisplayUnitName(inst, unitNameFromMetadata);

                const trayLabel = inst.trayTypeName || (inst.trayType ? `${inst.trayType}-hole` : "Not tray-based");

                // Prefer actualSowingDate (when sowing was recorded); fall back to scheduled
                // so the supervisor still sees something for pre-actual rows.
                const sowingDateRaw = inst.actualSowingDate || inst.scheduledSowingDate;
                const sowingDateLabel = sowingDateRaw
                  ? format(parseISO(sowingDateRaw), "dd MMM yyyy")
                  : "—";

                return (
                  <TableRow key={inst.id}>
                    <TableCell className="font-mono text-sm">{inst.instructionNumber}</TableCell>
                    <TableCell>{inst.cropTypeName}</TableCell>
                    <TableCell>{inst.variety}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{trayLabel}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{sowingDateLabel}</TableCell>
                    <TableCell>
                      {formatQuantity(quantityGerminated)} {unitName}
                    </TableCell>
                    <TableCell className="font-semibold text-green-700 dark:text-green-400">
                      {formatQuantity(stockQty)} {unitName}
                    </TableCell>
                    <TableCell className="font-semibold text-amber-700 dark:text-amber-400">
                      {formatQuantity(reserveQty)} {unitName}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {inst.updatedAt ? format(parseISO(inst.updatedAt), "dd MMM yyyy") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {canAcknowledge ? (
                        <Button size="sm" onClick={() => handleAcknowledge(inst)}>
                          Acknowledge Receipt
                        </Button>
                      ) : (
                        <span className="text-sm text-muted-foreground">View only</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {selected && (
        <AcknowledgeReceiptDialog
          instruction={selected}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};
